import { useMemo, type ReactNode } from "react";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Building2,
  DollarSign,
  Download,
  FileText,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  User,
} from "lucide-react";
import {
  formatDonationDate,
  formatPaymentStatusLabel,
  getDonationAmount,
  getDonationCampaignLabel,
  getDonationDonorName,
  getDonationReceiptNo,
  getDonationTypeLabel,
  getPaymentStatus,
  paymentStatusBadgeClass,
  type DonationDetailRecord,
  type DonationPayment,
} from "@project/types/donation-detail";

type DonationDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donation: DonationDetailRecord | null;
  loading: boolean;
  onDownloadReceipt: (donation: DonationDetailRecord) => void;
};

export function DonationDetailDialog({
  open,
  onOpenChange,
  donation,
  loading,
  onDownloadReceipt,
}: DonationDetailDialogProps) {
  const paymentHistory = useMemo<DonationPayment[]>(() => {
    if (!donation) {
      return [];
    }
    if (donation.mode === "offline") {
      return [
        {
          id: -1,
          razorpay_order_id: "",
          razorpay_payment_id: null,
          amount: String(donation.amount ?? "0"),
          currency: "INR",
          status: getPaymentStatus(donation),
          created_at: donation.created_at,
          payment_method: donation.payment_mode ?? "—",
          receipt_no: getDonationReceiptNo(donation),
        },
      ];
    }
    return donation.payments ?? [];
  }, [donation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="border-b px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <span className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                <DollarSign className="h-5 w-5 text-primary" />
              </span>
              Donation Details
            </DialogTitle>
            {donation && !loading && (
              <Badge variant="secondary" className="capitalize">
                {donation.mode ?? "offline"}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="animate-pulse font-medium text-muted-foreground">
              Fetching donation details...
            </p>
          </div>
        ) : donation ? (
          <>
            <div className="max-h-[min(80vh,730px)] overflow-y-auto">
              <div className="bg-primary/5 border-b px-6 py-5">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0 space-y-1 sm:flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                      Transaction amount
                    </p>
                    <p className="text-2xl font-semibold sm:text-3xl">
                      ₹{getDonationAmount(donation).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
                    <SummaryItem
                      label="Date"
                      value={formatDonationDate(
                        donation.created_at ?? donation.donation_date
                      )}
                    />
                    <SummaryItem
                      label="Purpose"
                      value={getDonationTypeLabel(donation)}
                    />
                    <SummaryItem
                      label="Campaign / Purpose"
                      value={getDonationCampaignLabel(donation)}
                    />
                  </div>
                </div>
                {donation.scheme_institution_name?.trim() && (
                  <div className="border-border/70 mt-5 border-t pt-4">
                    <SummaryItem
                      label="Institution name"
                      value={donation.scheme_institution_name.trim()}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                <InfoCard
                  title="Donor information"
                  icon={<User className="h-4 w-4" />}
                >
                  <DetailItem
                    icon={<User className="h-3.5 w-3.5" />}
                    label="Full name"
                    value={getDonationDonorName(donation)}
                  />
                  <DetailItem
                    icon={<Mail className="h-3.5 w-3.5" />}
                    label="Email address"
                    value={donation.email ?? "—"}
                  />
                  <DetailItem
                    icon={<Phone className="h-3.5 w-3.5" />}
                    label="Phone number"
                    value={donation.phone ?? donation.donor_mobile ?? "—"}
                  />
                  <DetailItem
                    icon={<Building2 className="h-3.5 w-3.5" />}
                    label="Organization"
                    value={donation.organization ?? "—"}
                  />
                  <DetailItem
                    icon={<FileText className="h-3.5 w-3.5" />}
                    label="PAN number"
                    value={donation.pan_number ?? "—"}
                  />
                </InfoCard>

                <InfoCard title="Address" icon={<MapPin className="h-4 w-4" />}>
                  <DetailItem
                    icon={<MapPin className="h-3.5 w-3.5" />}
                    label="Address line 1"
                    value={donation.address_line1 ?? "N/A"}
                  />
                  <DetailItem
                    icon={<MapPin className="h-3.5 w-3.5" />}
                    label="Address line 2"
                    value={donation.address_line2 ?? "N/A"}
                  />
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="District"
                      value={String(
                        donation.district_name ?? donation.district ?? "N/A"
                      )}
                    />
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="City"
                      value={String(
                        donation.city_name ?? donation.city ?? "N/A"
                      )}
                    />
                  </div>
                  <DetailItem
                    icon={<MapPin className="h-3.5 w-3.5" />}
                    label="Pincode"
                    value={donation.pincode ?? "N/A"}
                  />
                </InfoCard>

                <div className="md:col-span-2">
                  <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold">
                    <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                      <MessageSquare className="h-4 w-4 text-primary" />
                    </span>
                    Message from donor
                  </h3>
                  <div className="border-y py-4 pl-4 pr-4">
                    <p className="border-l-[3px] border-primary pl-4 text-sm font-medium leading-relaxed text-muted-foreground">
                      &quot;
                      {donation.message ??
                        "No special message provided with this donation."}
                      &quot;
                    </p>
                  </div>
                </div>

                <div className="space-y-3 md:col-span-2">
                  <h3 className="flex items-center gap-2.5 text-base font-bold">
                    <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                      <FileText className="h-4 w-4 text-primary" />
                    </span>
                    Payment history
                  </h3>
                  <div className="overflow-x-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Receipt</TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paymentHistory.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="py-6 text-center text-muted-foreground"
                            >
                              No payment records found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          paymentHistory.map(payment => (
                            <TableRow key={payment.id}>
                              <TableCell>{payment.receipt_no ?? "—"}</TableCell>
                              <TableCell className="font-mono text-xs">
                                {payment.razorpay_payment_id ?? "—"}
                              </TableCell>
                              <TableCell className="capitalize">
                                {payment.payment_method ?? "—"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={`capitalize ${paymentStatusBadgeClass(payment.status)}`}
                                >
                                  {formatPaymentStatusLabel(payment.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                ₹
                                {parseFloat(
                                  payment.amount || "0"
                                ).toLocaleString("en-IN")}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-3 border-t px-6 py-4 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
              <Button
                type="button"
                className="gap-2"
                onClick={() => onDownloadReceipt(donation)}
              >
                <Download className="h-4 w-4" />
                Download receipt
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="truncate text-sm font-semibold" title={value}>
        {value}
      </p>
    </div>
  );
}

function InfoCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold">
        <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
          <span className="text-primary">{icon}</span>
        </span>
        {title}
      </h3>
      <hr className="mb-4 border-border" />
      <div className="grid gap-5">{children}</div>
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}
